import React, { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../components/ui/alert';
import { toast } from 'sonner';
import { 
  Upload, 
  Download, 
  FileSpreadsheet, 
  Loader2, 
  CheckCircle2,
  XCircle,
  AlertCircle,
  FileArchive,
  FileText,
  Trash2,
  Info
} from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BulkImport = () => {
  const { token } = useAuth();
  const [csvFile, setCsvFile] = useState(null);
  const [zipFile, setZipFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  // Download template CSV
  const downloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const response = await axios.get(`${API_URL}/api/documents/bulk-import/template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'bulk_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded successfully');
    } catch (error) {
      toast.error('Failed to download template');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  // Handle CSV/Excel file selection
  const handleCsvChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!['csv', 'xlsx', 'xls'].includes(ext)) {
        toast.error('Please select a CSV or Excel file (.csv, .xlsx, .xls)');
        return;
      }
      setCsvFile(file);
      setImportResults(null);
    }
  };

  // Handle ZIP file selection
  const handleZipChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (ext !== 'zip') {
        toast.error('Please select a ZIP file');
        return;
      }
      setZipFile(file);
    }
  };

  // Clear files
  const clearFiles = () => {
    setCsvFile(null);
    setZipFile(null);
    setImportResults(null);
  };

  // Run import
  const runImport = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV or Excel file');
      return;
    }

    setImporting(true);
    setImportResults(null);

    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      if (zipFile) {
        formData.append('zip_file', zipFile);
      }

      const response = await axios.post(
        `${API_URL}/api/documents/bulk-import`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setImportResults(response.data);
      
      if (response.data.failed === 0) {
        toast.success(`Successfully imported ${response.data.successful} documents`);
      } else if (response.data.successful > 0) {
        toast.warning(`Imported ${response.data.successful} documents, ${response.data.failed} failed`);
      } else {
        toast.error(`Import failed: ${response.data.failed} errors`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  // Download import report as CSV
  const downloadReport = () => {
    if (!importResults) return;

    const headers = ['Row', 'Document Name', 'Document Number', 'Status', 'Error/Warning'];
    const rows = importResults.details.map(d => [
      d.row,
      d.document_name,
      d.document_number || '',
      d.status,
      d.error || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `import_report_${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();

    toast.success('Report downloaded');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'success':
        return <Badge className="bg-green-500/10 text-green-600">Success</Badge>;
      case 'failed':
        return <Badge className="bg-red-500/10 text-red-600">Failed</Badge>;
      default:
        return <Badge className="bg-yellow-500/10 text-yellow-600">Warning</Badge>;
    }
  };

  return (
    <div className="animate-fade-in" data-testid="bulk-import-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Bulk Import</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Import multiple documents from CSV or Excel files
          </p>
        </div>
      </div>

      {/* Instructions Card */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Info className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <CardTitle className="text-lg">How to Import Documents</CardTitle>
              <CardDescription>Follow these steps for a successful bulk import</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
            <li><strong>Download the template</strong> - Get the CSV template with all available columns</li>
            <li><strong>Fill in your data</strong> - Add your documents to the template. <em>Document Name</em>, <em>File Path</em>, and <em>File Name</em> are mandatory</li>
            <li><strong>Prepare your files</strong> - Either:
              <ul className="list-disc list-inside ml-6 mt-1">
                <li>Pack all document files in a ZIP archive, OR</li>
                <li>Place files in a folder accessible from the server (use File Path column)</li>
              </ul>
            </li>
            <li><strong>Upload and import</strong> - Select your CSV/Excel file and optional ZIP, then click Import</li>
          </ol>
          
          <div className="mt-4 p-3 bg-muted/50 rounded-lg">
            <p className="text-sm font-medium mb-2">Column Reference:</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
              <div><Badge variant="outline" className="text-red-600">Required</Badge> document_name</div>
              <div><Badge variant="outline" className="text-red-600">Required</Badge> file_name</div>
              <div><Badge variant="outline">Optional</Badge> file_path</div>
              <div><Badge variant="outline">Optional</Badge> document_number</div>
              <div><Badge variant="outline">Optional</Badge> description</div>
              <div><Badge variant="outline">Optional</Badge> project_name</div>
              <div><Badge variant="outline">Optional</Badge> document_type_code</div>
              <div><Badge variant="outline">Optional</Badge> revision_code</div>
              <div><Badge variant="outline">Optional</Badge> discipline_code</div>
              <div><Badge variant="outline">Optional</Badge> approval_code</div>
              <div><Badge variant="outline">Optional</Badge> status_code</div>
              <div><Badge variant="outline">Optional</Badge> tags (semicolon separated)</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upload Card */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Upload className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle>Upload Files</CardTitle>
                <CardDescription>Select your import file and optional document archive</CardDescription>
              </div>
            </div>
            <Button variant="outline" onClick={downloadTemplate} disabled={downloadingTemplate} data-testid="download-template-btn">
              {downloadingTemplate ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Download Template
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            {/* CSV/Excel Upload */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4" />
                CSV or Excel File <span className="text-red-500">*</span>
              </Label>
              <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                <Input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleCsvChange}
                  className="hidden"
                  id="csv-upload"
                  data-testid="csv-file-input"
                />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  {csvFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <FileText className="w-8 h-8 text-green-500" />
                      <div className="text-left">
                        <p className="font-medium">{csvFile.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(csvFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <FileSpreadsheet className="w-10 h-10 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        Click to select CSV or Excel file
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        .csv, .xlsx, .xls
                      </p>
                    </>
                  )}
                </label>
              </div>
            </div>

            {/* ZIP Upload */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <FileArchive className="w-4 h-4" />
                ZIP Archive (Optional)
              </Label>
              <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                <Input
                  type="file"
                  accept=".zip"
                  onChange={handleZipChange}
                  className="hidden"
                  id="zip-upload"
                  data-testid="zip-file-input"
                />
                <label htmlFor="zip-upload" className="cursor-pointer">
                  {zipFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <FileArchive className="w-8 h-8 text-blue-500" />
                      <div className="text-left">
                        <p className="font-medium">{zipFile.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(zipFile.size / (1024 * 1024)).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <FileArchive className="w-10 h-10 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        Click to select ZIP file with documents
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Contains files referenced in CSV
                      </p>
                    </>
                  )}
                </label>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t">
            {(csvFile || zipFile) && (
              <Button variant="outline" onClick={clearFiles} data-testid="clear-files-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                Clear Files
              </Button>
            )}
            <Button 
              onClick={runImport} 
              disabled={!csvFile || importing}
              data-testid="run-import-btn"
            >
              {importing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Start Import
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results Card */}
      {importResults && (
        <Card data-testid="import-results">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  importResults.failed === 0 
                    ? 'bg-green-500/10' 
                    : importResults.successful > 0 
                      ? 'bg-yellow-500/10' 
                      : 'bg-red-500/10'
                }`}>
                  {importResults.failed === 0 ? (
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  ) : importResults.successful > 0 ? (
                    <AlertCircle className="w-5 h-5 text-yellow-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-500" />
                  )}
                </div>
                <div>
                  <CardTitle>Import Results</CardTitle>
                  <CardDescription>
                    {importResults.successful} of {importResults.total_rows} documents imported successfully
                  </CardDescription>
                </div>
              </div>
              <Button variant="outline" onClick={downloadReport} data-testid="download-report-btn">
                <Download className="w-4 h-4 mr-2" />
                Download Report
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="p-4 rounded-lg bg-muted/50 text-center">
                <p className="text-2xl font-bold">{importResults.total_rows}</p>
                <p className="text-sm text-muted-foreground">Total Rows</p>
              </div>
              <div className="p-4 rounded-lg bg-green-500/10 text-center">
                <p className="text-2xl font-bold text-green-600">{importResults.successful}</p>
                <p className="text-sm text-muted-foreground">Successful</p>
              </div>
              <div className="p-4 rounded-lg bg-red-500/10 text-center">
                <p className="text-2xl font-bold text-red-600">{importResults.failed}</p>
                <p className="text-sm text-muted-foreground">Failed</p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-6">
              <div className="flex justify-between text-sm mb-1">
                <span>Import Progress</span>
                <span>{Math.round((importResults.successful / importResults.total_rows) * 100)}%</span>
              </div>
              <Progress 
                value={(importResults.successful / importResults.total_rows) * 100} 
                className="h-2"
              />
            </div>

            {/* Details Table */}
            <div className="border rounded-lg max-h-[400px] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky top-0 bg-background w-16">Row</TableHead>
                    <TableHead className="sticky top-0 bg-background">Document Name</TableHead>
                    <TableHead className="sticky top-0 bg-background">Document Number</TableHead>
                    <TableHead className="sticky top-0 bg-background w-24">Status</TableHead>
                    <TableHead className="sticky top-0 bg-background">Error/Warning</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {importResults.details.map((detail, idx) => (
                    <TableRow key={idx} className={detail.status === 'failed' ? 'bg-red-500/5' : ''}>
                      <TableCell className="font-mono text-sm">{detail.row}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{detail.document_name}</TableCell>
                      <TableCell className="font-mono text-sm">{detail.document_number || '-'}</TableCell>
                      <TableCell>{getStatusBadge(detail.status)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[300px]">
                        {detail.error || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BulkImport;
